import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "leaflet/dist/leaflet.css";
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css";
import "leaflet-defaulticon-compatibility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faBed,
  faPoundSign,
  faArrowLeft,
  faComputerMouse,
} from "@fortawesome/free-solid-svg-icons";
import useThemeStore from "../store/themeStore";
import useTokenStore from "../store/store";
import { ThemeProps } from "../utils/theme";
import {
  getNewBuildProperty,
  NewBuildPropertyItem,
  NewBuildUnit,
  PropertyExtraResult,
} from "../utils/properties";
import { baseUrl } from "../utils";
import placeholderImage from "../images/photo-coming-soon-300x300-1-square.jpg";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import ImageSlider from "../components/property/Slider";
import FinancialSection from "../pages/components/FinancialSection";
import ComparablesSection from "../pages/components/ComparablesSection";
import LocalAreaInfoSection from "../pages/components/LocalAreaInformation";
import AdvertisingCarousel from "../pages/components/AdvertisingCarousel";

const formatPrice = (price?: string): string => {
  if (!price) return "";
  const num = parseFloat(price);
  if (isNaN(num)) return "£" + price;
  return "£" + Math.round(num).toLocaleString("en-GB");
};

const UNIT_STATUS = {
  AVAILABLE: "available",
  SOLD: "sold",
  HELD: "held",
  UNKNOWN: "unknown",
} as const;

type UnitStatusValue = (typeof UNIT_STATUS)[keyof typeof UNIT_STATUS];

const UNIT_STATUS_LABEL: Record<UnitStatusValue, string> = {
  [UNIT_STATUS.AVAILABLE]: "Available",
  [UNIT_STATUS.SOLD]: "Sold",
  [UNIT_STATUS.HELD]: "Held",
  [UNIT_STATUS.UNKNOWN]: "Available",
};

const UNIT_STATUS_COLOUR: Record<UnitStatusValue, string> = {
  [UNIT_STATUS.AVAILABLE]: "#16a34a",
  [UNIT_STATUS.SOLD]: "#dc2626",
  [UNIT_STATUS.HELD]: "#d97706",
  [UNIT_STATUS.UNKNOWN]: "#16a34a",
};

function UnitStatusBadge({ status }: { status?: string }) {
  const safeStatus = (status || UNIT_STATUS.AVAILABLE) as UnitStatusValue;
  const label = UNIT_STATUS_LABEL[safeStatus] ?? "Available";
  const colour =
    UNIT_STATUS_COLOUR[safeStatus] ?? UNIT_STATUS_COLOUR[UNIT_STATUS.AVAILABLE];

  return (
    <span
      className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{
        background: colour + "22",
        color: colour,
        border: `1px solid ${colour}44`,
      }}
    >
      {label}
    </span>
  );
}

/** Adapts a NewBuildPropertyItem into the PropertyExtraResult shape expected by shared components */
function toPropertyExtraResult(
  nb: NewBuildPropertyItem
): PropertyExtraResult {
  return {
    results: {
      price: Number(nb.price_from) || 0,
      region: nb.region || "",
      marketComparable: nb.onDistrictCompare || null,
      streetComparable: nb.onStreetCompare || null,
      saleComparable: nb.onForSaleCompare || null,
      rentComparable: nb.onForRentCompare || null,
      nearestStation: nb.stations || null,
      nearestSchool: nb.schools || null,
      latitude: nb.latitude ?? null,
      longitude: nb.longitude ?? null,
      postcode: nb.postcode || "",
      bedrooms: 0,
      propertyOf: nb.title || nb.development || "",
      propertyDescription: nb.headline || "",
      fullAddress: nb.address || nb.location || "",
      images: nb.images || null,
      reserved: false,
      tenureType: nb.tenureType || "",
      depositeProperty: nb.deposite || 0,
      averageRent: nb.averageRent || 0,
      rentGrossYield: nb.grossYield || 0,
      houseType: nb.propertyType || "",
    } as any,
    message: "",
    theme: { color1: "", color2: "", logo: "", paymentAccept: false },
  };
}

function FaqAccordion({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!faqs || faqs.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mt-4">
      {faqs.map((faq, i) => (
        <div
          key={i}
          className="border border-gray-200 rounded-lg overflow-hidden"
        >
          <button
            className="w-full flex justify-between items-center gap-3 px-4 py-3 text-sm font-medium text-gray-800 text-left hover:bg-gray-50 transition-colors"
            style={{ background: openIndex === i ? "#f1f5f9" : "#f8fafc" }}
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
          >
            {faq.question}
            <span className="text-lg font-light text-gray-500 flex-shrink-0">
              {openIndex === i ? "−" : "+"}
            </span>
          </button>
          {openIndex === i && (
            <div className="px-4 py-3 text-sm text-gray-600 leading-relaxed bg-white border-t border-gray-200">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface FinanceFormData {
  "First Name": string;
  "Last Name": string;
  Phone: string;
  Email: string;
  "Property Address": string;
  "Website Referred From": string;
}

function NewBuildDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const getThemes = useThemeStore(
    (state: { theme: ThemeProps | undefined }) => state.theme
  ) as any;
  const getToken = useTokenStore((state) => state.token);
  const [property, setProperty] = useState<NewBuildPropertyItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [financeSubmitStatus, setFinanceSubmitStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [financeMessage, setFinanceMessage] = useState("");
  const aboutRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [unitStatusFilter, setUnitStatusFilter] = useState<string | null>(UNIT_STATUS.AVAILABLE);

  useEffect(() => {
    const checkScrollable = () => {
      if (aboutRef.current) {
        setIsScrollable(
          aboutRef.current.scrollHeight > aboutRef.current.clientHeight
        );
      }
    };
    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  });

  useEffect(() => {
    if (!id) return;
    const fetchProperty = async () => {
      setLoading(true);
      const result = await getNewBuildProperty(id);
      setProperty(result.results);
      setLoading(false);
    };
    fetchProperty();
  }, [id]);

  const handleFinanceApplication = async () => {
    if (!getToken) {
      navigate(`/login?redirectTo=/new-builds/${id}`);
      return;
    }

    setFinanceSubmitStatus("loading");
    try {
      const profileResponse = await fetch(`${baseUrl}/user/profile`, {
        method: "GET",
        headers: {
          Authorization:
            typeof getToken === "string" && getToken
              ? `Bearer ${(getToken as string).replace("B9I9N9456", "")}`
              : "",
          "Content-Type": "application/json",
        },
      });

      if (!profileResponse.ok) {
        throw new Error("Failed to fetch user profile");
      }

      const userProfile = await profileResponse.json();

      const formData: FinanceFormData = {
        "First Name": userProfile.first_name || "",
        "Last Name": userProfile.last_name || "",
        Phone: userProfile.phone || "",
        Email: userProfile.email || "",
        "Property Address":
          property?.address || property?.location || "",
        "Website Referred From":
          getThemes?.name || "Whitelabel Website",
      };

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (getToken && typeof getToken === "string") {
        const cleanToken = (getToken as string).replace("B9I9N9456", "");
        headers["x-auth-token"] = cleanToken;
        headers["Authorization"] = `Bearer ${cleanToken}`;
      }

      const response = await fetch(`${baseUrl}/whitelabel/financeapply`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.msg || "Failed to submit finance application"
        );
      }

      setFinanceSubmitStatus("success");
      setFinanceMessage(
        "Your finance application has been submitted successfully."
      );
    } catch (error) {
      setFinanceSubmitStatus("error");
      setFinanceMessage(
        error instanceof Error
          ? error.message
          : "An error occurred while submitting your application."
      );
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex top-0 bottom-0 m-auto justify-center">
        <div
          className="align-middle animate-spin inline-block w-8 h-8 border-[2px] border-primarycolor border-t-transparent text-secondarycolor rounded-full"
          role="status"
          aria-label="loading"
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 text-gray-500">
        <p>Development not found.</p>
        <button
          onClick={() => navigate("/new-builds")}
          className="text-sm font-medium hover:opacity-75"
          style={{ color: getThemes?.color1 || "#004AAE" }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to New Builds
        </button>
      </div>
    );
  }

  const availableUnits = (property.units || []).filter(
    (u) => u.status === UNIT_STATUS.AVAILABLE || u.status === UNIT_STATUS.UNKNOWN || !u.status
  );
  const soldUnits = (property.units || []).filter(
    (u) => u.status === UNIT_STATUS.SOLD
  );
  const heldUnits = (property.units || []).filter(
    (u) => u.status === UNIT_STATUS.HELD
  );
  const totalUnits = property.units?.length ?? 0;

  const adaptedProperty = toPropertyExtraResult(property);

  const priceDisplay = (() => {
    if (property.price_from && property.price_to) {
      return `${formatPrice(property.price_from)} – ${formatPrice(property.price_to)}`;
    }
    if (property.price_from) return `From ${formatPrice(property.price_from)}`;
    if (property.price_to) return `Up to ${formatPrice(property.price_to)}`;
    return "";
  })();

  return (
    <>
      <Helmet>
        <title>
          {property.title || property.development || "New Build Development"}
        </title>
      </Helmet>

      <div className="Properties">
        <div className="container mx-auto p-4">
          <div className="pt-20 mt-10"></div>

          {/* ============== Main Grid (matches Property.tsx) ============== */}
          <div className="grid lg:grid-cols-3 grid-cols-1 gap-4 pt-5 mt-10">
            {/* Left Column: Image + Headline (2/3) */}
            <div className="lg:col-span-2 border-gray-300 border bg-white rounded-lg shadow-lg p-4 hover:shadow-xl transition duration-150 ease-in-out">
              <div className="pb-5">
                {property.images && property.images.length > 0 ? (
                  <ImageSlider images={property.images} />
                ) : (
                  <div className="w-full h-64 bg-[#F3F3F3] flex items-center justify-center">
                    <img
                      src={placeholderImage}
                      alt="Coming soon"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}

                {/* Title and Price */}
                <div className="mt-4 text-left">
                  <h1 className="text-2xl lg:text-3xl font-bold mb-2">
                    {property.title ||
                      property.development ||
                      "New Build Development"}
                  </h1>

                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                    <span className="text-lg sm:text-xl lg:text-2xl break-words max-w-full sm:max-w-[70%]">
                      {property.location || property.address || ""}
                    </span>

                    {priceDisplay && (
                      <span className="text-2xl lg:text-3xl font-bold">
                        {priceDisplay}
                      </span>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-2 mb-2 overflow-hidden">
                  {property.developer && (
                    <span
                      className="text-xs font-bold py-1 px-2 rounded-md"
                      style={{
                        background: getThemes?.color2 || "#004AAE",
                        color: getThemes?.txtColor || "#ffffff",
                      }}
                    >
                      {property.developer}
                    </span>
                  )}
                  {property.tenureType && (
                    <span
                      className="text-xs font-bold py-1 px-2 rounded-md"
                      style={{
                        background: getThemes?.color2 || "#004AAE",
                        color: getThemes?.txtColor || "#ffffff",
                      }}
                    >
                      {property.tenureType}
                    </span>
                  )}
                  {property.propertyType && (
                    <span
                      className="text-xs font-bold py-1 px-2 rounded-md"
                      style={{
                        background: getThemes?.color2 || "#004AAE",
                        color: getThemes?.txtColor || "#ffffff",
                      }}
                    >
                      {property.propertyType}
                    </span>
                  )}
                  {totalUnits > 0 && (
                    <span
                      className="text-xs font-bold py-1 px-2 rounded-md"
                      style={{
                        background: getThemes?.color2 || "#004AAE",
                        color: getThemes?.txtColor || "#ffffff",
                      }}
                    >
                      {totalUnits} Units
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Action Buttons (1/3) */}
            <div className="flex flex-col space-y-4 h-full">
              <div
                className="relative w-full"
                style={{
                  backgroundImage: `url(${backgroundPattern})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "50px 50px",
                }}
              >
                <div className="bg-white border-gray-300 border rounded-lg shadow-lg p-8 hover:shadow-xl transition duration-150 ease-in-out">
                  <div className="flex flex-col space-y-4 h-full">
                    {/* Reserve Button */}
                    <button
                      className="py-2.5 w-full max-w-xs mx-auto rounded-full transition ease-in-out delay-150 hover:scale-110 duration-300"
                      style={{
                        background: getThemes?.color2 || "#004AAE",
                        color: "#f2f9fa",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (!getToken) {
                          navigate(`/login?redirectTo=/new-builds/${id}`);
                        }
                      }}
                    >
                      Reserve
                    </button>

                    {/* Apply for Finance Button */}
                    <div className="h-[38px]">
                      <button
                        className="py-2.5 w-full max-w-xs mx-auto rounded-full transition ease-in-out delay-150 hover:scale-110 duration-300"
                        style={{
                          background: getThemes?.color2 || "#004AAE",
                          color: "#f2f9fa",
                          cursor: "pointer",
                        }}
                        onClick={handleFinanceApplication}
                        disabled={financeSubmitStatus === "loading"}
                      >
                        {financeSubmitStatus === "loading"
                          ? "Submitting..."
                          : "Apply for Finance"}
                      </button>
                    </div>

                    {/* Finance Status Message */}
                    {financeSubmitStatus !== "idle" && (
                      <div
                        className={`text-center text-sm ${
                          financeSubmitStatus === "success"
                            ? "text-green-600"
                            : financeSubmitStatus === "error"
                            ? "text-red-600"
                            : ""
                        }`}
                      >
                        {financeMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* About This Development + FAQs (right side) */}
              {(property.headline || (property.faqs && property.faqs.length > 0)) && (
                <div className="bg-white border-gray-300 border rounded-lg shadow-lg p-4 hover:shadow-xl transition duration-150 ease-in-out">
                  <h4 className="text-xl border-b border-gray-300 font-semibold mt-2 pb-4 pt-4 text-center">
                    About This Development
                  </h4>
                  <div className="relative" style={{ height: '375px' }}>
                    <div ref={aboutRef} className="overflow-y-auto h-full">
                      {property.headline && (
                        <pre className="pt-4 font-poppins text-xs max-w-full textWrap"
                          style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                        >
                          {property.headline}
                        </pre>
                      )}
                      {property.faqs && property.faqs.length > 0 && (
                        <FaqAccordion faqs={property.faqs} />
                      )}
                    </div>
                    {isScrollable && (
                      <div className="absolute bottom-0 right-0 p-2 animate-bounce bg-white bg-opacity-75 rounded-tl-lg">
                        <FontAwesomeIcon icon={faComputerMouse} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============== Key Features (selling_points) ============== */}
        {property.selling_points && property.selling_points.length > 0 && (
          <div className="container mx-auto p-4">
            <div className="w-full bg-white border-gray-300 border shadow-sm rounded-xl font-roboto">
              <div className="border-b border-gray-300 rounded-t-xl py-3 px-4 md:py-4 md:px-5">
                <h3 className="mt-1 text-2xl font-semibold tracking-normal text-primarycolor text-left">
                  Key Features
                </h3>
              </div>
              <div className="p-4 md:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.selling_points.map((point, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="flex-shrink-0"
                        style={{ color: getThemes?.color2 || "#004AAE" }}
                      />
                      <span className="text-sm text-gray-700">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============== Financial Section ============== */}
        <div
          className="relative w-full"
          style={{
            backgroundImage: `url(${backgroundPattern})`,
            backgroundRepeat: "repeat",
            backgroundSize: "75px 75px",
          }}
        >
          {getThemes && (
            <FinancialSection
              property={adaptedProperty}
              getThemes={getThemes}
            />
          )}
        </div>

        {/* ============== Unit Availability ============== */}
        {totalUnits > 0 && (() => {
          const coreKeys = new Set(["unit", "beds", "price", "status", "available", "_id", "__v"]);
          const extraKeys: string[] = [];
          for (const u of property.units || []) {
            for (const k of Object.keys(u)) {
              if (!coreKeys.has(k) && !extraKeys.includes(k)) {
                extraKeys.push(k);
              }
            }
          }

          const normaliseStatus = (s?: string) =>
            !s || s === UNIT_STATUS.UNKNOWN ? UNIT_STATUS.AVAILABLE : s;

          const filteredUnits = unitStatusFilter
            ? property.units!.filter(
                (u) => normaliseStatus(u.status) === unitStatusFilter
              )
            : property.units!;

          const formatExtraValue = (val: any): string => {
            if (val === null || val === undefined) return "—";
            if (typeof val === "boolean") return val ? "Yes" : "No";
            if (typeof val === "number") return val.toLocaleString();
            return String(val);
          };

          const formatColumnName = (key: string): string =>
            key.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").replace(/^\w/, (c) => c.toUpperCase()).trim();

          return (
          <div className="container mx-auto p-4">
            <div className="w-full bg-white border-gray-300 border shadow-sm rounded-xl font-roboto">
              <div className="border-b border-gray-300 rounded-t-xl py-3 px-4 md:py-4 md:px-5">
                <h3 className="mt-1 text-2xl font-semibold tracking-normal text-primarycolor text-left">
                  Unit Availability
                </h3>
              </div>
              <div className="p-4 md:p-5">
                {/* Filter badges */}
                <div className="flex gap-2 flex-wrap mb-4 items-center">
                  <button
                    onClick={() => setUnitStatusFilter(unitStatusFilter === UNIT_STATUS.AVAILABLE ? null : UNIT_STATUS.AVAILABLE)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-all ${
                      unitStatusFilter === UNIT_STATUS.AVAILABLE
                        ? "bg-green-600 text-white ring-2 ring-green-300"
                        : "bg-green-100 text-green-600 hover:bg-green-200"
                    }`}
                  >
                    {availableUnits.length} Available
                  </button>
                  <button
                    onClick={() => setUnitStatusFilter(unitStatusFilter === UNIT_STATUS.SOLD ? null : UNIT_STATUS.SOLD)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-all ${
                      unitStatusFilter === UNIT_STATUS.SOLD
                        ? "bg-red-600 text-white ring-2 ring-red-300"
                        : "bg-red-100 text-red-600 hover:bg-red-200"
                    }`}
                  >
                    {soldUnits.length} Sold
                  </button>
                  <button
                    onClick={() => setUnitStatusFilter(unitStatusFilter === UNIT_STATUS.HELD ? null : UNIT_STATUS.HELD)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-all ${
                      unitStatusFilter === UNIT_STATUS.HELD
                        ? "bg-yellow-600 text-white ring-2 ring-yellow-300"
                        : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                    }`}
                  >
                    {heldUnits.length} Held
                  </button>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                    {totalUnits} Total
                  </span>
                  {unitStatusFilter && (
                    <button
                      onClick={() => setUnitStatusFilter(null)}
                      className="text-xs font-medium px-3 py-1 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 cursor-pointer transition-all"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">
                          Unit
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">
                          <FontAwesomeIcon icon={faBed} className="mr-1" /> Beds
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">
                          <FontAwesomeIcon icon={faPoundSign} className="mr-1" /> Price
                        </th>
                        {extraKeys.map((key) => (
                          <th key={key} className="px-4 py-3 text-left font-semibold text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">
                            {formatColumnName(key)}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUnits.map((unit: NewBuildUnit, i: number) => (
                        <tr
                          key={i}
                          className="border-b border-gray-100 last:border-b-0"
                        >
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {unit.unit || `Unit ${i + 1}`}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {unit.beds ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {unit.price
                              ? `£${Number(unit.price).toLocaleString()}`
                              : "—"}
                          </td>
                          {extraKeys.map((key) => (
                            <td key={key} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {formatExtraValue(unit[key])}
                            </td>
                          ))}
                          <td className="px-4 py-3">
                            <UnitStatusBadge status={unit.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* ============== Comparables Section ============== */}
        <ComparablesSection
          property={adaptedProperty}
          getThemes={getThemes}
        />

        {/* ============== Local Area / Additional Information ============== */}
        <div
          className="relative w-full"
          style={{
            backgroundImage: `url(${backgroundPattern})`,
            backgroundRepeat: "repeat",
            backgroundSize: "75px 75px",
          }}
        >
          <LocalAreaInfoSection
            property={adaptedProperty}
            getThemes={getThemes}
          />
        </div>

        {/* ============== Other Properties Carousel ============== */}
        {property.region && (
          <div className="Properties">
            <AdvertisingCarousel
              region={property.region}
              limit={6}
            />
          </div>
        )}

        {/* ============== Disclaimer ============== */}
        <div
          className="relative w-full"
          style={{
            backgroundImage: `url(${backgroundPattern})`,
            backgroundRepeat: "repeat",
            backgroundSize: "75px 75px",
          }}
        >
          <div
            className="w-full py-4"
            style={{
              margin: "0 auto",
              padding: "20px",
              marginBottom: "10px",
            }}
          >
            <div
              className="flex max-w-7xl m-auto py-5 lg:p-0 bg-white rounded-lg shadow-lg p-4"
              style={{ backgroundColor: "white" }}
            >
              <div className="w-full text-left bg-white border-gray-300 border shadow-sm rounded-xl font-roboto p-5">
                <div className="rounded-t-xl mb-3">
                  <h3 className="mt-1 text-2xl font-semibold tracking-normal text-primarycolor text-left">
                    Disclaimer
                  </h3>
                </div>
                <p>
                  This is for your information only – you shouldn't view this as
                  legal advice, tax advice, investment advice, or any advice at
                  all. This information does not constitute a solicitation,
                  recommendation, endorsement in this or in any other
                  jurisdiction. While we've tried to make sure this information
                  is accurate and up to date, things can change, so it shouldn't
                  be viewed as totally comprehensive. We always recommend you
                  seek out independent advice before making any investment
                  decisions.
                </p>
                <br />
                <p>
                  With respect to gross yield & rental income, these values are
                  either provided by our partners or provided by comparing
                  similar properties within the same geographical location.
                  Gross yield is calculated using the standard industry formula
                  by taking the annual rental income of the property and
                  dividing it by the purchase price of the property. Any
                  information pertaining to the EPC has been retrieved via the UK
                  government public database of EPC certificates.
                </p>
                <p>
                  Stock listed may on occasion be sold or withdrawn by Vendors at
                  short notice, and thus be no longer available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewBuildDetail;
